import { cn } from "@newxsystems/shared";
import { getBalancedGridColumnCount } from "@templates/design-system";
import { getComponent } from "../../../../client";
import { DaylightMedia } from "../DaylightMedia/DaylightMedia";
import type { ILogoCloudGridProps } from "./LogoCloudGrid.props";

const desktopColumns = {
  1: "md:grid-cols-1",
  2: "md:grid-cols-2",
  3: "md:grid-cols-3",
  4: "md:grid-cols-4",
} as const;

/**
 * The rule on the right of a cell has to stop at the last column of its own row.
 * Below `md` every row is two wide; from `md` it is `desktopColumnCount`. The
 * narrow range is `max-md` scoped because an `nth-child` selector outranks a
 * plain breakpoint utility, so without it the two-column rule would still be in
 * force once the grid widens — and the last column of a row would carry a rule
 * that hangs down the edge of the block.
 */
const rightEdges = {
  1: "[&:nth-child(2n)]:border-r-0 md:border-r-0",
  2: "[&:nth-child(2n)]:border-r-0",
  3: "max-md:[&:nth-child(2n)]:border-r-0 md:[&:nth-child(3n)]:border-r-0",
  4: "max-md:[&:nth-child(2n)]:border-r-0 md:[&:nth-child(4n)]:border-r-0",
} as const;

export const LogoCloudGrid = ({ items }: ILogoCloudGridProps) => {
  const Link = getComponent("Link");
  const desktopColumnCount = getBalancedGridColumnCount(items.length);
  return (
    // The closing rule is an inset shadow rather than a border on the grid: it
    // spans every column even when the last row is short, and the last row's own
    // rule lands on top of it instead of doubling beside it.
    <div
      className={cn(
        "grid border-t border-border shadow-[inset_0_-1px_0_var(--color-border)]",
        items.length === 1 ? "grid-cols-1" : "grid-cols-2",
        desktopColumns[desktopColumnCount],
      )}
      data-reveal="stagger"
    >
      {items.map((item, index) => {
        const logo = (
          <DaylightMedia
            className="h-8 w-40 bg-transparent"
            framed={false}
            imageClassName="object-contain invert mix-blend-screen opacity-45 transition-opacity duration-200 group-hover:opacity-100"
            media={item.logo}
            sizes="144px"
          />
        );
        return (
          <div
            className={cn(
              "flex min-h-28 items-center justify-center border-b border-r border-border px-6",
              rightEdges[desktopColumnCount],
            )}
            key={index}
          >
            {item.logoLink ? (
              <Link
                aria-label={item.logoLink.accessibilityLabel || undefined}
                className="group block focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                href={item.logoLink.href}
                rel={item.logoLink.openInNewTab ? "noopener noreferrer" : undefined}
                target={item.logoLink.openInNewTab ? "_blank" : undefined}
              >
                {logo}
              </Link>
            ) : (
              logo
            )}
          </div>
        );
      })}
    </div>
  );
};

LogoCloudGrid.displayName = "Content::LogoCloudGrid";
